import { landscapePalette } from "../world/landscapePalette";
/**
 * Player-centred, north-up surface minimap.
 *
 * Terrain comes from the same analytic sampler as the visible world, so its
 * coastlines, rivers, biomes and carved roads agree with the ground. The
 * expensive raster is cached and scrolls under the fixed player arrow.
 */
import * as THREE from "three";
import type { TerrainSample } from "@hyperlight/shared";
import type { Planet } from "../world/planet";
import type { Place } from "../world/settlement/places";

export interface GroundMapMarker {
  /** Planet-local surface position. */
  local: THREE.Vector3;
  color: string;
  shape?: "dot" | "diamond" | "house";
  size?: number;
}

const RASTER_SIZE = 104;
const OVERSCAN = 1.14;
const TWO_PI = Math.PI * 2;
const clamp01 = (n: number): number => Math.max(0, Math.min(1, n));
const smoothstep = (a: number, b: number, n: number): number => {
  const t = clamp01((n - a) / (b - a));
  return t * t * (3 - 2 * t);
};
const mix = (a: number, b: number, t: number): number => a + (b - a) * t;

function mixRgb(out: [number, number, number], color: readonly number[], t: number): void {
  out[0] = mix(out[0], color[0], t);
  out[1] = mix(out[1], color[1], t);
  out[2] = mix(out[2], color[2], t);
}

/** Cartographic approximation of the terrain material's major color layers. */
function mapColor(
  planet: Planet,
  sample: TerrainSample,
  out: [number, number, number],
): { water: boolean; road: boolean } {
  const def = planet.def;
  const palette = landscapePalette(def);
  const t = clamp01(sample.temp * 0.5 + 0.5) * 2.999;
  const m = clamp01(sample.moist * 0.5 + 0.5) * 1.999;
  const tr = Math.floor(t);
  const mc = Math.floor(m);
  const tb = t - tr;
  const mb = m - mc;
  const table = palette.table;
  const a = table[tr * 3 + mc];
  const b = table[tr * 3 + mc + 1];
  const c = table[(tr + 1) * 3 + mc];
  const d = table[(tr + 1) * 3 + mc + 1];
  out[0] = mix(mix(a[0], b[0], mb), mix(c[0], d[0], mb), tb);
  out[1] = mix(mix(a[1], b[1], mb), mix(c[1], d[1], mb), tb);
  out[2] = mix(mix(a[2], b[2], mb), mix(c[2], d[2], mb), tb);

  const surfaceR = def.radius + sample.h * planet.sampler.amplitude;
  const ocean = def.seaRadius !== null && surfaceR <= def.seaRadius;
  const river = !ocean && sample.river > 0.36;
  if (ocean) {
    const depth = clamp01((def.seaRadius! - surfaceR) / Math.max(20, planet.sampler.amplitude * 0.16));
    out[0] = def.seaColor[0] * mix(0.86, 0.55, depth);
    out[1] = def.seaColor[1] * mix(0.9, 0.62, depth);
    out[2] = def.seaColor[2] * mix(1.08, 0.82, depth);
  } else if (river) {
    mixRgb(out, palette.water, smoothstep(0.32, 0.72, sample.river) * 0.92);
  } else if (def.seaRadius !== null) {
    const seaRel = (def.seaRadius - def.radius) / planet.sampler.amplitude;
    mixRgb(out, palette.sand, 1 - smoothstep(0.015, 0.075, sample.h - seaRel));
  }

  const cold = 1 - smoothstep(-0.65, -0.35, sample.temp);
  const altitude = smoothstep(def.climate.snowline, def.climate.snowline + 0.15, sample.h);
  if (!ocean && !river) mixRgb(out, palette.snow, Math.max(cold, altitude));

  // Forest masses must read at map scale even when individual trees cannot.
  if (def.hasFlora && !ocean && !river) {
    const forest = smoothstep(-0.5, -0.15, sample.temp)
      * (1 - smoothstep(0.45, 0.8, sample.temp))
      * smoothstep(-0.1, 0.4, sample.moist);
    out[0] *= mix(1, 0.55, forest * 0.55);
    out[1] *= mix(1, 0.78, forest * 0.55);
    out[2] *= mix(1, 0.55, forest * 0.55);
  }

  const road = !ocean && !river && sample.road < 1.4;
  if (road) mixRgb(out, [0.56, 0.485, 0.375], 1 - smoothstep(-0.4, 1.4, sample.road));
  if (!ocean && sample.town > 0.05) mixRgb(out, [0.66, 0.59, 0.47], sample.town * 0.18);
  return { water: ocean || river, road };
}

export class GroundMinimap {
  private readonly terrain = document.createElement("canvas");
  private readonly terrainCtx: CanvasRenderingContext2D;
  private readonly centerDir = new THREE.Vector3(0, 1, 0);
  private readonly north = new THREE.Vector3(0, 0, -1);
  private readonly east = new THREE.Vector3(1, 0, 0);
  private readonly focusDir = new THREE.Vector3();
  private readonly localForward = new THREE.Vector3();
  private readonly sampleDir = new THREE.Vector3();
  private readonly spinAxis = new THREE.Vector3();
  private readonly rgb: [number, number, number] = [0, 0, 0];
  private readonly heights = new Float32Array(RASTER_SIZE * RASTER_SIZE);
  private readonly water = new Uint8Array(RASTER_SIZE * RASTER_SIZE);
  private readonly roads = new Uint8Array(RASTER_SIZE * RASTER_SIZE);
  private readonly sample: TerrainSample = {
    h: 0, temp: 0, moist: 0, river: 0, mountain: 0,
    flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8,
  };
  private cachedPlanet = "";
  private cachedRadius = 0;
  private valid = false;

  constructor() {
    this.terrain.width = RASTER_SIZE;
    this.terrain.height = RASTER_SIZE;
    const ctx = this.terrain.getContext("2d", { alpha: false });
    if (!ctx) throw new Error("2D canvas unavailable for ground minimap");
    this.terrainCtx = ctx;
  }

  invalidate(): void {
    this.valid = false;
  }

  private setBasis(planet: Planet, focusWorld: THREE.Vector3): void {
    this.centerDir.copy(focusWorld).sub(planet.pose.center)
      .applyQuaternion(planet.pose.invQuat).normalize();
    this.spinAxis.set(...planet.def.spin.axis).normalize();
    this.north.copy(this.spinAxis).addScaledVector(this.centerDir, -this.spinAxis.dot(this.centerDir));
    // At a pole, retain a deterministic meridian instead of flipping the compass.
    if (this.north.lengthSq() < 1e-8) {
      this.north.set(0, 0, 1).addScaledVector(this.centerDir, -this.centerDir.z);
      if (this.north.lengthSq() < 1e-8) this.north.set(1, 0, 0);
    }
    this.north.normalize();
    this.east.crossVectors(this.north, this.centerDir).normalize();
  }

  private rasterize(planet: Planet, focusWorld: THREE.Vector3, radiusM: number): void {
    this.setBasis(planet, focusWorld);
    this.cachedPlanet = planet.def.id;
    this.cachedRadius = radiusM;
    this.valid = true;

    const image = this.terrainCtx.createImageData(RASTER_SIZE, RASTER_SIZE);
    const data = image.data;
    const sampleRadius = radiusM * OVERSCAN;
    const diameter = sampleRadius * 2;
    const planetR = planet.def.radius;
    for (let y = 0; y < RASTER_SIZE; y++) {
      const northM = (0.5 - (y + 0.5) / RASTER_SIZE) * diameter;
      for (let x = 0; x < RASTER_SIZE; x++) {
        const eastM = ((x + 0.5) / RASTER_SIZE - 0.5) * diameter;
        this.sampleDir.copy(this.centerDir)
          .addScaledVector(this.east, eastM / planetR)
          .addScaledVector(this.north, northM / planetR).normalize();
        planet.sampler.sample(this.sampleDir.x, this.sampleDir.y, this.sampleDir.z, this.sample, true);
        const k = y * RASTER_SIZE + x;
        const px = k * 4;
        const flags = mapColor(planet, this.sample, this.rgb);
        this.heights[k] = this.sample.h * planet.sampler.amplitude;
        this.water[k] = flags.water ? 1 : 0;
        this.roads[k] = flags.road ? 1 : 0;
        data[px] = Math.round(clamp01(this.rgb[0]) * 255);
        data[px + 1] = Math.round(clamp01(this.rgb[1]) * 255);
        data[px + 2] = Math.round(clamp01(this.rgb[2]) * 255);
        data[px + 3] = 255;
      }
    }

    // North-west hillshade turns the biome raster into readable relief.
    const metresPerPixel = diameter / RASTER_SIZE;
    for (let y = 1; y < RASTER_SIZE - 1; y++) {
      for (let x = 1; x < RASTER_SIZE - 1; x++) {
        const k = y * RASTER_SIZE + x;
        if (this.water[k]) continue;
        const dx = (this.heights[k + 1] - this.heights[k - 1]) / (metresPerPixel * 2);
        const dy = (this.heights[k + RASTER_SIZE] - this.heights[k - RASTER_SIZE]) / (metresPerPixel * 2);
        let shade = 0.94 + Math.max(-0.16, Math.min(0.16, (-dx + dy) * 0.22));
        if (this.roads[k]) shade = mix(shade, 1, 0.6);
        const px = k * 4;
        data[px] = Math.min(255, Math.round(data[px] * shade));
        data[px + 1] = Math.min(255, Math.round(data[px + 1] * shade));
        data[px + 2] = Math.min(255, Math.round(data[px + 2] * shade));
      }
    }
    this.terrainCtx.putImageData(image, 0, 0);
  }

  private project(local: THREE.Vector3, cx: number, cy: number, pxPerM: number): [number, number] {
    return [cx + local.dot(this.east) * pxPerM, cy - local.dot(this.north) * pxPerM];
  }

  draw(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    planet: Planet,
    focusWorld: THREE.Vector3,
    forwardWorld: THREE.Vector3,
    radiusM: number,
    places: readonly Place[],
    markers: readonly GroundMapMarker[] = [],
  ): void {
    this.focusDir.copy(focusWorld).sub(planet.pose.center)
      .applyQuaternion(planet.pose.invQuat).normalize();
    const dot = Math.max(-1, Math.min(1, this.centerDir.dot(this.focusDir)));
    const movedM = this.valid && this.cachedPlanet === planet.def.id
      ? Math.acos(dot) * planet.def.radius
      : Infinity;
    if (!this.valid || this.cachedPlanet !== planet.def.id
      || Math.abs(this.cachedRadius - radiusM) > 1 || movedM > radiusM * 0.035) {
      this.rasterize(planet, focusWorld, radiusM);
      this.focusDir.copy(this.centerDir);
    }

    ctx.clearRect(0, 0, w, h);
    const cx = w / 2;
    const cy = h / 2;
    const mapR = Math.min(w, h) / 2 - 4;
    const pxPerM = mapR / radiusM;
    const focusLocal = this.sampleDir.copy(this.focusDir).multiplyScalar(planet.def.radius);
    const focusX = focusLocal.dot(this.east) * pxPerM;
    const focusY = focusLocal.dot(this.north) * pxPerM;
    const drawSize = mapR * 2 * OVERSCAN;

    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, mapR, 0, TWO_PI);
    ctx.clip();
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(this.terrain, cx - drawSize / 2 - focusX, cy - drawSize / 2 + focusY, drawSize, drawSize);

    const vignette = ctx.createRadialGradient(cx, cy, mapR * 0.38, cx, cy, mapR);
    vignette.addColorStop(0, "rgba(3, 9, 14, 0)");
    vignette.addColorStop(0.78, "rgba(3, 9, 14, 0.05)");
    vignette.addColorStop(1, "rgba(3, 9, 14, 0.52)");
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, w, h);

    const drawMarker = (local: THREE.Vector3, color: string, shape: GroundMapMarker["shape"], size = 3): void => {
      const [x, y] = this.project(local, cx - focusX, cy + focusY, pxPerM);
      if (Math.hypot(x - cx, y - cy) > mapR - size - 3) return;
      ctx.fillStyle = color;
      ctx.strokeStyle = "rgba(4, 10, 15, 0.88)";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      if (shape === "diamond") {
        ctx.moveTo(x, y - size); ctx.lineTo(x + size, y);
        ctx.lineTo(x, y + size); ctx.lineTo(x - size, y); ctx.closePath();
      } else if (shape === "house") {
        ctx.moveTo(x, y - size - 1); ctx.lineTo(x + size + 1, y - 0.5);
        ctx.lineTo(x + size - 0.5, y + size); ctx.lineTo(x - size + 0.5, y + size);
        ctx.lineTo(x - size - 1, y - 0.5); ctx.closePath();
      } else {
        ctx.arc(x, y, size, 0, TWO_PI);
      }
      ctx.stroke();
      ctx.fill();
    };
    for (const place of places) drawMarker(place.local, "#ffd78d", "house", 3.1 + Math.min(1.8, place.tier * 0.28));
    for (const marker of markers) drawMarker(marker.local, marker.color, marker.shape, marker.size);

    // The player's arrow remains fixed while terrain and POIs scroll below it.
    this.localForward.copy(forwardWorld).applyQuaternion(planet.pose.invQuat);
    const heading = Math.atan2(this.localForward.dot(this.east), this.localForward.dot(this.north));
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(heading);
    ctx.shadowColor = "rgba(90, 220, 255, 0.85)";
    ctx.shadowBlur = 6;
    ctx.fillStyle = "#e8fbff";
    ctx.strokeStyle = "#173d52";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, -8.5); ctx.lineTo(5.5, 6.5); ctx.lineTo(0, 3.6);
    ctx.lineTo(-5.5, 6.5); ctx.closePath();
    ctx.stroke();
    ctx.fill();
    ctx.restore();
    ctx.restore();

    // Compass and scale sit above the clipped terrain.
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "bold 9px 'SF Mono', Menlo, monospace";
    ctx.fillStyle = "rgba(226, 246, 255, 0.9)";
    ctx.shadowColor = "rgba(0, 0, 0, 0.9)";
    ctx.shadowBlur = 3;
    ctx.fillText("N", cx, 11);
    const niceScale = radiusM >= 2500 ? 1000 : 500;
    const scalePx = niceScale * pxPerM;
    const sy = h - 12;
    ctx.strokeStyle = "rgba(226, 246, 255, 0.85)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx - scalePx / 2, sy); ctx.lineTo(cx + scalePx / 2, sy);
    ctx.moveTo(cx - scalePx / 2, sy - 2); ctx.lineTo(cx - scalePx / 2, sy + 2);
    ctx.moveTo(cx + scalePx / 2, sy - 2); ctx.lineTo(cx + scalePx / 2, sy + 2);
    ctx.stroke();
    ctx.font = "7px 'SF Mono', Menlo, monospace";
    ctx.fillText(niceScale >= 1000 ? "1 KM" : "500 M", cx, sy - 5);
    ctx.shadowBlur = 0;
  }
}
